import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Landmark, Copy, Eye, EyeOff, CircleDashed, ShieldCheck, RefreshCw,
} from "lucide-react";
import VerificationFrame from "./VerificationFrame";

/**
 * VerqoAccountCard — the member's own account and routing number.
 *
 * Every verified business holds a real checking account at Verqo's partner
 * bank. Buyers fund their wallet into it and sellers receive from anyone who
 * can send ACH, a wire or an instant payment — including marketplaces such as
 * Amazon, Etsy and Walmart. The details are shown in full because members hand
 * them to their own customers for AR.
 */
export default function VerqoAccountCard({ onChange, compact = false, role = "seller" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/shield/account");
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || { code: "unavailable" } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy.");
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6" data-testid="verqo-account-loading">
        <div className="h-4 w-40 animate-pulse rounded bg-foreground/10" />
        <div className="mt-4 h-8 w-64 animate-pulse rounded bg-foreground/5" />
      </div>
    );
  }

  const acct = data?.account;

  if (!acct) {
    const code = data?.error?.code;
    return (
      <>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6"
             data-testid="verqo-account-pending">
          <div className="flex items-start gap-3">
            <CircleDashed className="mt-0.5 h-5 w-5 text-amber-700" />
            <div className="flex-1">
              <div className="display text-lg text-amber-900">
                Your account number is one step away
              </div>
              <p className="mt-1 max-w-xl text-sm text-amber-800/80">
                {code === "account_not_open"
                  ? (role === "buyer"
                      ? "As soon as your business is verified, you get a checking account and routing number in your business's name — fund your wallet straight from your own bank."
                      : "As soon as your business is verified, you get a checking account and routing number in your business's name — so buyers, and marketplaces like Amazon or Walmart, can pay you directly.")
                  : "We could not load your account just now. Try again in a moment."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => setVerifyOpen(true)}
                        className="h-10 rounded-full bg-amber-900 text-amber-50 hover:bg-amber-900/90"
                        data-testid="verqo-account-verify-btn">
                  <ShieldCheck className="mr-1.5 h-4 w-4" /> Verify my business
                </Button>
                <Button variant="ghost" onClick={load} className="h-10 rounded-full"
                        data-testid="verqo-account-retry-btn">
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>
        <VerificationFrame open={verifyOpen} onOpenChange={setVerifyOpen}
                           onApproved={() => { load(); onChange?.(); }} />
      </>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-white p-6" data-testid="verqo-account-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Landmark className="mt-0.5 h-5 w-5 text-foreground/45" />
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">
              Your account
            </div>
            <div className="display mt-1 text-xl tracking-tight">
              {acct.account_title}
            </div>
          </div>
        </div>
        <span className="chip rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700">
          Open
        </span>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">
            Account number
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="mono text-base tabular-nums" data-testid="verqo-account-number">
              {reveal && acct.account_number ? acct.account_number : acct.account_number_masked}
            </span>
            {acct.account_number && (
              <>
                <button onClick={() => setReveal((v) => !v)}
                        className="text-foreground/35 hover:text-foreground"
                        data-testid="verqo-account-reveal-btn"
                        aria-label={reveal ? "Hide account number" : "Show account number"}>
                  {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button onClick={() => copy(acct.account_number, "Account number")}
                        className="text-foreground/35 hover:text-foreground"
                        data-testid="verqo-account-copy-btn" aria-label="Copy account number">
                  <Copy className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">
            Routing number
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="mono text-base tabular-nums" data-testid="verqo-routing-number">
              {acct.routing_number}
            </span>
            <button onClick={() => copy(acct.routing_number, "Routing number")}
                    className="text-foreground/35 hover:text-foreground"
                    data-testid="verqo-routing-copy-btn" aria-label="Copy routing number">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {!compact && (
        <p className="mt-5 text-sm leading-relaxed text-foreground/60">
          {role === "buyer"
            ? "Fund your wallet by sending an ACH, a wire or an instant payment to these details from your own bank — it lands in your Verqo balance, usually the same day."
            : "Give these details to anyone who owes you money — buyers, or a marketplace such as Amazon, Etsy or Walmart. ACH, wires and instant payments all land in your Verqo balance."}
        </p>
      )}

      <div className="mt-4 border-t border-border pt-4 text-[11px] leading-relaxed text-foreground/45"
           data-testid="verqo-bank-details">
        <div>
          {data.bank?.name}
          {data.bank?.address ? ` · ${data.bank.address}` : ""}
        </div>
        {data.fdic && <div className="mt-1">{data.fdic}</div>}
      </div>
    </div>
  );
}
